import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip } from 'react-leaflet';
import UpdateCenter from './UpdateCenter';
import { useMediaQuery, CircularProgress } from '@mui/material';
import { useAuthorizationContext } from '../context/AuthorizationContext';
import NextBusDeparture from './NextBusDeparture';
import BusStopsComponent from './BusStopsComponent';
import FocusBusToggleComponent from './FocusBusToggleComponent';

const LiveTrackingComponent = ({ lineId, alignment, lineSettings }) => {
  let { busStopIdOrigin, busStopIdDest, lineColor, splitRouteAt, routeId, centerMapAt } = lineSettings[lineId];
  const { jwt, headers, getAuthToken } = useAuthorizationContext();

  const [vehiclesPosition, setVehiclesPosition] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [points, setPoints] = useState([]);
  const [centerToBusLive, setCenterToBusLive] = useState(false);
  const [busOnFocusID, setBusOnFocusID] = useState();
  const isMobile = useMediaQuery('(max-width:800px)');

  const updateDataInterval = 8; // seconds
  let routePoints = alignment === 'to' ? points.slice(0, splitRouteAt) : points.slice(splitRouteAt);

  const getPoints = async () => {
    try {
      // use this to cache the route points
      const cachedPoints = sessionStorage.getItem(`routePoints_${lineId}`);
      if (cachedPoints) {
        setPoints(JSON.parse(cachedPoints));
      } else {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/points/line/${lineId}/route/${routeId}`, { headers });
        setPoints(data.points);
        console.log(data);
        sessionStorage.setItem(`routePoints_${lineId}`, JSON.stringify(data.points));
      }
    } catch (error) {
      console.log(error);

      // handle authorization error
      if (error.request.status === 401) {
        getAuthToken();
      }
    }
    setLoading(false);
  };

  const getLiveBusPosition = async (stopId) => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/live/${stopId}`, { headers });
      console.log('request status', data);

      if (data.status && vehiclesPosition.length > 0) {
        return [];
        // console.log('response status ', data.status, data.title);
      }

      // use this to always get the first bus that has the same route id as the selected route
      let livePositions = [];
      for (const bus of data.vehicles) {
        if (bus.lineCode === lineId) {
          if (bus.latitude != 0 || bus.longitude != 0) livePositions.push(bus);
        }
      }
      return livePositions;
    } catch (error) {
      console.log('Live possition is not available for stopId: ', stopId);

      // console.log(error);

      // handle authorization error
      if (error.request) {
        if (error.request.status === 401) {
          getAuthToken();
        }
      }
      return [];
    }
  };
  // use this logic to avoid mixing buses of the "to" alignement with the "from" alignment
  const handleVehiclePositions = async () => {
    if (alignment === 'to') {
      // setVehiclesPosition(await getLiveBusPosition(busStopIdDest));
      let temp = await getLiveBusPosition(busStopIdDest);
      setVehiclesPosition(temp);
      console.log(temp);
    } else if (alignment === 'from') {
      let positionsToIgnore = await getLiveBusPosition(busStopIdDest);
      let liveVehiclePositions = await getLiveBusPosition(busStopIdOrigin);

      liveVehiclePositions = liveVehiclePositions.filter((vehicle) => !positionsToIgnore.some((live) => live.vehicleCode === vehicle.vehicleCode));

      setVehiclesPosition(liveVehiclePositions);
    }
  };

  useEffect(() => {
    handleVehiclePositions();
    const intervalId = setInterval(() => {
      setCount((count) => count + 1);
    }, updateDataInterval * 1000);

    return () => clearInterval(intervalId);
  }, [count, lineId, alignment, jwt]);

  // for dev purposes
  useEffect(() => {
    Array.isArray(vehiclesPosition) && vehiclesPosition > 0 && console.log('at least one bus is on the route, vehiclesPosition', vehiclesPosition);
  }, [vehiclesPosition]);

  // use this to clear the markers when the alignment or lineId is changed
  const notInitialRender = useRef(false);
  useEffect(() => {
    if (notInitialRender.current) {
      setVehiclesPosition([]);
      console.log('erasing vehiclesPosition...');
    } else {
      notInitialRender.current = true;
    }
  }, [alignment, lineId]);

  useEffect(() => {
    getPoints();
  }, [lineId, jwt]);

  useEffect(() => {
    setCenterToBusLive(false);
  }, [alignment, lineId]);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      <NextBusDeparture lineId={lineId} vehiclesPosition={vehiclesPosition} />
      <div className='focusBusToggle'>
        <FocusBusToggleComponent
          vehiclesPosition={vehiclesPosition}
          setBusOnFocusID={setBusOnFocusID}
          setCenterToBusLive={setCenterToBusLive}
          centerToBusLive={centerToBusLive}
        />
      </div>
      <div id='map'>
        {!loading && Array.isArray(points) && points.length > 0 && (
          <MapContainer
            center={centerMapAt}
            zoom={isMobile ? 12 : 14}
            tap={'false'}
            scrollWheelZoom={true}
            maxBounds={[
              [41.18459151591197, 24.986343628827058],
              [41.08400380717848, 24.835968262616117],
            ]}
          >
            <TileLayer
              attribution='&amp;copy <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
              url='https://tile.openstreetmap.org/{z}/{x}/{y}.png'
            />
            {Array.isArray(vehiclesPosition) &&
              vehiclesPosition.length > 0 &&
              vehiclesPosition.map((item, index) => (
                <Marker key={index} position={[item.latitude, item.longitude]}>
                  <Tooltip direction='auto' permanent={true} opacity={1} backgroundColor='black'>
                    {item.vehicleCode.slice(-5).replace('_', ':')}
                  </Tooltip>
                </Marker>
              ))}
            <BusStopsComponent routeId={routeId} alignment={alignment} busStopIdDest={busStopIdDest} />
            <Polyline pathOptions={{ color: lineColor }} positions={routePoints} />
            <UpdateCenter
              vehiclesPosition={vehiclesPosition}
              centerToBusLive={centerToBusLive}
              setCenterToBusLive={setCenterToBusLive}
              points={points}
              isMobile={isMobile}
              centerMapAt={centerMapAt}
              busOnFocusID={busOnFocusID}
            />
          </MapContainer>
        )}
      </div>
    </>
  );
};

export default LiveTrackingComponent;
